import { timeToTimestampFormatter } from '@helpers/timeToTimestampFormatter';
import { IBusySlot } from '@api/trial/types';
import { ITimeRange } from '@pages/Appointment/pages/CreateAppointment/components/DetailsForm/types';
import { TWeekday, TWorkingDays } from '@pages/Physicians/components/PhysiciansTable/types';

export const getFreeOfDay = (
  duration: number,
  allSlots: ITimeRange[] = [],
  busySlots: IBusySlot[] = [],
): ITimeRange[] => {
  const freeIntervals: ITimeRange[] = [];

  for (const { from, to } of allSlots) {
    const slotFrom = timeToTimestampFormatter(from).getTime();

    const slotTo = timeToTimestampFormatter(to).getTime();

    let currentTimestamp = slotFrom;

    while (currentTimestamp + duration * 60 * 1000 <= slotTo) {
      const intervalStart = new Date(currentTimestamp);

      const intervalEnd = new Date(currentTimestamp + duration * 60 * 1000);

      const formattedFrom = intervalStart.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      const formattedTo = intervalEnd.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      if (!isTimeSlotBusy(intervalStart, intervalEnd, busySlots)) {
        freeIntervals.push({
          from: formattedFrom,
          to: formattedTo,
        });
      }

      currentTimestamp += 15 * 60 * 1000;
    }
  }

  return freeIntervals;
};

function isTimeSlotBusy(slotStart: Date, slotEnd: Date, busySlots: IBusySlot[] = []): boolean {
  const slotFrom = slotStart.getTime();

  const slotTo = slotEnd.getTime();

  return busySlots.some(({ from, to }) => {
    const busyFrom = timeToTimestampFormatter(from).getTime();

    const busyTo = timeToTimestampFormatter(to).getTime();

    return slotFrom < busyTo && slotTo > busyFrom;
  });
}

export const getOverlappingTimeRanges = (
  scheduleLocation: TWorkingDays,
  schedulePhysician: TWorkingDays,
  day: string,
): ITimeRange[] => {
  const slots1 = scheduleLocation?.[day.toLowerCase() as TWeekday] || [];

  const slots2 = schedulePhysician?.[day.toLowerCase() as TWeekday] || [];

  const overlaps: ITimeRange[] = [];

  for (const s1 of slots1) {
    const start1 = timeToTimestampFormatter(s1.from).getTime();

    const end1 = timeToTimestampFormatter(s1.to).getTime();

    for (const s2 of slots2) {
      const start2 = timeToTimestampFormatter(s2.from).getTime();

      const end2 = timeToTimestampFormatter(s2.to).getTime();

      const overlapStart = Math.max(start1, start2);

      const overlapEnd = Math.min(end1, end2);

      if (overlapStart < overlapEnd) {
        overlaps.push({
          from: new Date(overlapStart).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }),
          to: new Date(overlapEnd).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }),
        });
      }
    }
  }

  return overlaps;
};

const weekdays: TWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const getUnavailableTimeRanges = (
  scheduleLocation: TWorkingDays,
  schedulePhysician: TWorkingDays,
): TWorkingDays => {
  const result: TWorkingDays = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  for (const weekday of weekdays) {
    const locationSlots = scheduleLocation?.[weekday] || [];

    const physicianSlots = schedulePhysician?.[weekday] || [];

    const overlaps: ITimeRange[] = [];

    for (const locSlot of locationSlots) {
      const locStart = timeToTimestampFormatter(locSlot.from).getTime();

      const locEnd = timeToTimestampFormatter(locSlot.to).getTime();

      for (const docSlot of physicianSlots) {
        const docStart = timeToTimestampFormatter(docSlot.from).getTime();

        const docEnd = timeToTimestampFormatter(docSlot.to).getTime();

        const overlapStart = Math.max(locStart, docStart);

        const overlapEnd = Math.min(locEnd, docEnd);

        if (overlapStart < overlapEnd) {
          overlaps.push({
            from: new Date(overlapStart).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            }),
            to: new Date(overlapEnd).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            }),
          });
        }
      }
    }

    result[weekday] = overlaps;
  }

  return result;
};
